import { useGeckoPermissions } from 'features/permissions'
import { DrawerState, useRightDrawer } from 'features/rightDrawer'
import { useRolodexEntries } from 'features/rolodex'
import { useEffect, useMemo, useState } from 'react'
import {
  GeckoComponentDataFrame,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
} from 'types'

/**
 * A hook which uses useRolodexEntries() to load data (unit, site, formatters,
 * CDFS, markup layers, etc.) relevant to a given site ID, supplied from the
 * current route.
 * @param props       The site ID to load relevant data for
 */
export const useLandingPageData = (props: { siteID: string | undefined }) => {
  //----------------------------------------------------------------------------
  const { hasPermissions } = useGeckoPermissions()

  const {
    sites,
    getSite,
    getOrganization,
    organizations,
    isLoading: isLoadingSites,
    hasEverLoaded: hasEverLoadedSites,
    forceRefresh,
  } = useRolodexEntries({
    type: [RolodexEntryType.SITE, RolodexEntryType.ORGANIZATION],
    order_by: [{ key: 'name', descending: 'false' }],
  })

  const { site, sitesForOrg, organization } = useMemo(() => {
    const site = props.siteID
      ? // If there's a site ID in the URL, find that site
        getSite(props.siteID)
      : // Otherwise, redirect to the demo site if the user has access to
        // the demo and the first available site if the user doesn't
        (getSite('atlantic-deer-island') ?? sites.at(0))

    // If we didn't get a site based on the ID prop, assume it's an organization ID
    const organization = site
      ? getOrganization(site.tags.organization)
      : getOrganization(props.siteID)
    const sitesForOrg = sites.filter(
      (s) => s.tags.organization === organization?.id
    )
    return { site, sitesForOrg, organization }
  }, [props.siteID, sites, getSite, getOrganization])

  const {
    jobs,
    units,
    assetModels,
    inspections: unfilteredInspections,
    components,
    componentDataFrames: unsortedComponentDataFrames,
    componentDatasets: unsortedComponentDatasets,
    componentDefaults,
    isLoading: isLoadingSiteData,
    hasEverLoaded: hasEverLoadedSiteData,
    forceRefresh: secondForceRefresh,
  } = useRolodexEntries(
    !site
      ? []
      : {
          type: [
            RolodexEntryType.COMPONENT,
            RolodexEntryType.COMPONENT_DEFAULTS,
            RolodexEntryType.JOB,
            RolodexEntryType.INSPECTION,
            RolodexEntryType.UNIT,
            RolodexEntryType.ASSET_MODEL,
            RolodexEntryType.COMPONENT_DATA_FRAME,
            RolodexEntryType.FDE_COMPONENT_DATA_FRAME,
            RolodexEntryType.COMPONENT_DATASET,
          ],
          tags: { site: site.id },
        }
  )

  const { inspections, componentDataFrames, componentDatasets } =
    useMemo(() => {
      // Filter down to customer-visible markup layers and CDFs
      const componentDatasets = unsortedComponentDatasets.filter((cds) =>
        unfilteredInspections.some((ins) => cds.tags.inspection === ins.id)
      )
      const componentDataFrames = unsortedComponentDataFrames
        .filter(
          (cdf) =>
            !cdf.tags['component-dataset'] ||
            componentDatasets.some(
              (cds) => cds.id === cdf.tags['component-dataset']
            )
        )
        .sort(GeckoComponentDataFrame.GetDefaultSort(componentDatasets))

      const cdfInspectionIDs = new Set(
        componentDataFrames.map((cdf) => cdf.tags.inspection)
      )

      const inspections = !hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
        ? unfilteredInspections.filter((i) => cdfInspectionIDs.has(i.id))
        : unfilteredInspections

      return {
        inspections,
        componentDataFrames,
        componentDatasets,
      }
    }, [
      unfilteredInspections,
      unsortedComponentDataFrames,
      unsortedComponentDatasets,
      hasPermissions,
    ])

  // TODO: ENT-1266 -- Remove this when we are able to optimistically update
  // "This implementation kinda sucks."     --Tom Mori, July 2024
  // Goal here is to force refresh the data in the ForecastTopBar when a new Organization or Site is added
  // Since the Landing page (and useLandingPageData) exists as a child of the drawer, we have to react to
  // changes in state of the drawer itself. To prevent re-fetching the data every re-render that uses useLandingPageData
  // we set a state that says "the edit organization / site drawer was open", and "when it closes, refresh the data"
  const [forceRefreshData, setForceRefreshData] = useState(false)
  const { drawerState } = useRightDrawer()

  useEffect(() => {
    // A state of interest has been opened, let's prep the refresh
    if (
      (drawerState === DrawerState.EditGeckoOrganization ||
        drawerState === DrawerState.EditGeckoSite ||
        drawerState === DrawerState.EditGeckoInspection ||
        drawerState === DrawerState.EditGeckoUnit) &&
      !forceRefreshData
    ) {
      setForceRefreshData(true)
    }
    // The drawer has now been closed (after being in a state of interest), refresh the data and reset the variable
    if (drawerState === DrawerState.Closed && forceRefreshData) {
      forceRefresh()
      secondForceRefresh()
      setForceRefreshData(false)
    }
  }, [drawerState, forceRefreshData, forceRefresh, secondForceRefresh])

  //----------------------------------------------------------------------------
  return {
    organization,
    organizations,
    site,
    sites,
    sitesForOrg,
    jobs,
    units,
    assetModels,
    inspections,
    components,
    componentDefaults,
    componentDataFrames,
    componentDatasets,
    isLoading: isLoadingSites || isLoadingSiteData,
    hasEverLoaded: hasEverLoadedSites || hasEverLoadedSiteData,
    forceRefresh,
  }
}
